import { format } from "date-fns";
import React from "react";

import { IgAlert, IgChip, IgDialog, IgTypography } from "@intelligems/ig-theme";
import Stack from "@mui/material/Stack";

import type { WorkspaceVersion } from "../../../types";

/** Publish-a-previous-version confirm modal (danger variant). */
export interface RollbackConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (version: number) => void;
  /** The version being rolled back to, or null when closed. */
  targetVersion: number | null;
  versions: WorkspaceVersion[];
  isRollingBack: boolean;
  /** Domain copy for what switching the active version actually affects. */
  impactCopy: (version: number) => string;
}

const ReviewRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={2}
  >
    <IgTypography variant="body2" color="text.secondary">
      {label}
    </IgTypography>
    <Stack direction="row" alignItems="center" spacing={1}>
      {children}
    </Stack>
  </Stack>
);

/**
 * Publish-version confirm modal. Resolves the target version from the version
 * history to surface its published date and test status, and warns the operator
 * what confirming will change. Consumes {@link RollbackConfirmModalProps}.
 */
export const RollbackConfirmModal = ({
  open,
  onClose,
  onConfirm,
  targetVersion,
  versions,
  isRollingBack,
  impactCopy,
}: RollbackConfirmModalProps) => {
  const target =
    targetVersion !== null
      ? (versions.find((v) => v.version === targetVersion) ?? null)
      : null;

  const publishedDate = target?.publishedAtTs
    ? format(new Date(target.publishedAtTs), "PPp")
    : "Unknown";

  // A version can only have been published if it cleared all gates, so a
  // non-failed target reflects a passing suite.
  const testStatusPassed = target !== null && target.status !== "failed";

  const content = (
    <Stack spacing={2}>
      <IgAlert
        alertKey="publish-version-confirm-warning"
        severity="warning"
        title="This switches live checkout traffic"
        message={
          targetVersion !== null
            ? impactCopy(targetVersion)
            : "Select a version to publish."
        }
      />

      <Stack spacing={1.5}>
        <ReviewRow label="Target version">
          <IgTypography
            variant="body2"
            sx={{ fontFamily: "monospace", fontWeight: 600 }}
          >
            {targetVersion !== null ? `v${targetVersion}` : "—"}
          </IgTypography>
        </ReviewRow>

        <ReviewRow label="Published">
          <IgTypography variant="body2">{publishedDate}</IgTypography>
        </ReviewRow>

        <ReviewRow label="Test status">
          {testStatusPassed ? (
            <IgChip size="small" color="success" label="8/8 pass" />
          ) : (
            <IgChip size="small" color="error" label="Failed" />
          )}
        </ReviewRow>
      </Stack>
    </Stack>
  );

  return (
    <IgDialog
      showDialog={open}
      title="Publish version"
      handleClose={onClose}
      handleNoClick={onClose}
      handleYesClick={() => {
        if (targetVersion !== null) onConfirm(targetVersion);
      }}
      yesButtonText={
        targetVersion !== null ? `Publish v${targetVersion}` : "Publish"
      }
      maxWidth={"lg"}
      yesButtonColor="error"
      yesButtonDisabled={targetVersion === null}
      loading={isRollingBack}
      shouldNotClose
      content={content}
    />
  );
};
